import React, { useMemo } from "react";

import { MdCheck, MdOutlineNavigateBefore, MdSettings } from "react-icons/md";
import PageTitle from "../../components/PageTitle/PageTitle";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import useMobile from "../../hooks/useMobile";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import apiClient from "../../helpers/api-client";
import {
  API_ENDPOINT_UPDATE_PROFILE,
  BUTTON_STYLES,
  ICON_SIZES,
  SOURCES_DEFAULT_BG_COLOR,
  TRANSACTION_STATUSES,
} from "../../helpers/constants";
import { showNotification } from "../../helpers/utils";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import ColorPicker from "../../components/ColorPicker/ColorPicker";
import useCurrentUser from "../../hooks/useCurrentUser";
import { capitalize } from "lodash-es";

import moment from "moment";
import useDarkMode from "../../hooks/useDarkMode";
import InfoBlock from "../../components/InfoBlock/InfoBlock";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import useCollapsibles from "src/hooks/useCollapsibles";
import CustomSelect from "src/components/Select/CustomSelect";
import TextList, { ELinkType } from "src/components/TextList/TextList";
import { FILE_TYPES } from "src/components/ExportTransactionsModal/ExportTransactionsModal";
import { EButtonKind } from "src/components/Button/ButtonTypes";

const TransactionsColorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { colors, settings, resetCache } = useCurrentUser();
  const [transactionColors, setTransactionColors] = useState<any>(
    colors.transactions
  );
  const {
    collapsiblePreferences,
    updateCollapsible,
    isAppearancePageMobileCollapsibleOpen,
    toggleAppearancePageMobileCollapsible,
  } = useCollapsibles();
  const [balanceColors, setBalanceColors] = useState<any>(colors.balance);
  const { isEnabled, setEnabled } = useDarkMode();
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const [monthColors, setMonthColors] = useState<any>(colors.months);
  const [dayColors, setDayColors] = useState<any>(colors.days);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const { t } = useTranslation();
  const { state } = useLocation();

  const hasActiveBlock = !!state?.activeBlock;
  const daysBlockIsActive = state?.activeBlock === "days";
  const monthsBlockIsActive = state?.activeBlock === "months";

  const updateColors = () => {
    setIsUpdating(true);

    const formData = new FormData();

    formData.append(
      "settings",
      JSON.stringify({
        ...settings,
        colors: {
          ...settings.colors,
          transactions: transactionColors,
          months: monthColors,
          days: dayColors,
          balance: balanceColors,
          files: {},
          statuses: {},
        },
      })
    );

    apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        showNotification({
          message: "Cambios guardados",
          kind: "success",
        });

        await resetCache();

        setShowConfirmDialog(false);
        // onUpdate();
      })
      .catch((e) => {
        showNotification({
          message: "Error guardando los cambios",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const transactionStatuses = useMemo(() => {
    return TRANSACTION_STATUSES();
  }, [TRANSACTION_STATUSES]);

  const fileTypeValues = useMemo(() => Object.values(FILE_TYPES), []);

  return (
    <>
      <TwoSidebars
        right={
          isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
      >
        <div className={clsx(["flex", "w-full", "flex-col", "gap-0"])}>
          <PageTitle
            titleLink="/settings"
            icon={<MdSettings size={ICON_SIZES.MD} />}
            title={t("settings_page.title")}
            subtitle={t("Apariencia")}
            isLoading={false}
            rightSide={
              <>
                <ButtonGroup
                  align="right"
                  gap={1}
                  buttons={[
                    {
                      icon: <MdOutlineNavigateBefore size={ICON_SIZES.MD} />,
                      className: isMobile ? BUTTON_STYLES.HEADING : "",
                      tooltip: t("common_tooltips.back"),
                      onClick: () => {
                        navigate("/settings");
                      },
                    },
                    generalOptionsDropdown,
                  ]}
                />
              </>
            }
          />
          <div className={clsx(["flex", "gap-4", "flex-col"])}>
            <CollapsibleBlock
              uppercase
              title="Tema"
              open={collapsiblePreferences.appearanceThemeOpen}
              onChange={(isOpen: boolean) => {
                updateCollapsible({ appearanceThemeOpen: isOpen });
              }}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-col-reverse",
                  "gap-2",
                  "md:gap-4",
                  "text-sm",
                ])}
              >
                <InfoBlock
                  displayMobileIcon
                  title="Tema por dispositivo"
                  message="La configuración del tema es por dispositivo."
                />
                <div className={clsx(["flex", "w-full"])}>
                  <CustomSelect
                    label="Color"
                    options={[
                      {
                        value: "dark",
                        label: "Oscuro",
                      },
                      { value: "light", label: "Claro" },
                    ]}
                    value={isEnabled ? "dark" : "light"}
                    onChange={(e) => setEnabled(e?.value === "dark")}
                    required
                    isClearable={false}
                    useValueAsLabel={false}
                    isSearchable={false}
                  />
                </div>
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock
              uppercase
              open={collapsiblePreferences.appearanceTransactionsOpen}
              onChange={(isOpen: boolean) => {
                updateCollapsible({ appearanceTransactionsOpen: isOpen });
              }}
              title="Transacciones"
            >
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                <div className={clsx(["grid", "grid-cols-3", "gap-2"])}>
                  <div className={clsx(["flex", "items-center", "text-sm"])}>
                    Ingresos
                  </div>
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setTransactionColors({
                        ...transactionColors,
                        income: {
                          ...transactionColors.income,
                          bg: color.rgb,
                        },
                      });
                    }}
                    textColor={transactionColors.income.color}
                    color={transactionColors.income.bg}
                  />
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setTransactionColors({
                        ...transactionColors,
                        income: {
                          ...transactionColors.income,
                          color: color.rgb,
                        },
                      });
                    }}
                    textColor={transactionColors.income.bg}
                    color={transactionColors.income.color}
                  />
                </div>
                <div className={clsx(["grid", "grid-cols-3", "gap-2"])}>
                  <div className={clsx(["flex", "items-center", "text-sm"])}>
                    Gastos
                  </div>
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setTransactionColors({
                        ...transactionColors,
                        expenses: {
                          ...transactionColors.expenses,
                          bg: color.rgb,
                        },
                      });
                    }}
                    textColor={transactionColors.expenses.color}
                    color={transactionColors.expenses.bg}
                  />
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setTransactionColors({
                        ...transactionColors,
                        expenses: {
                          ...transactionColors.expenses,
                          color: color.rgb,
                        },
                      });
                    }}
                    textColor={transactionColors.expenses.bg}
                    color={transactionColors.expenses.color}
                  />
                </div>
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock
              uppercase
              open={collapsiblePreferences.appearanceBalanceOpen}
              onChange={(isOpen: boolean) => {
                updateCollapsible({ appearanceBalanceOpen: isOpen });
              }}
              title="Balance / Diferencia"
            >
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                <div className={clsx(["grid", "grid-cols-3", "gap-2"])}>
                  <div className={clsx(["flex", "items-center", "text-sm"])}>
                    Positivo
                  </div>
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setBalanceColors({
                        ...balanceColors,
                        positive: {
                          ...balanceColors.positive,
                          bg: color.rgb,
                        },
                      });
                    }}
                    textColor={balanceColors.positive.color}
                    color={balanceColors.positive.bg}
                  />
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setBalanceColors({
                        ...balanceColors,
                        positive: {
                          ...balanceColors.positive,
                          color: color.rgb,
                        },
                      });
                    }}
                    textColor={balanceColors.positive.bg}
                    color={balanceColors.positive.color}
                  />
                </div>
                <div className={clsx(["grid", "grid-cols-3", "gap-2"])}>
                  <div className={clsx(["flex", "items-center", "text-sm"])}>
                    Negativo
                  </div>
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setBalanceColors({
                        ...balanceColors,
                        negative: {
                          ...balanceColors.negative,
                          bg: color.rgb,
                        },
                      });
                    }}
                    textColor={balanceColors.negative.color}
                    color={balanceColors.negative.bg}
                  />
                  <ColorPicker
                    showText={false}
                    onChange={(color: any) => {
                      setBalanceColors({
                        ...balanceColors,
                        negative: {
                          ...balanceColors.negative,
                          color: color.rgb,
                        },
                      });
                    }}
                    textColor={balanceColors.negative.bg}
                    color={balanceColors.negative.color}
                  />
                </div>
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock
              uppercase
              open={collapsiblePreferences.appearanceMonthsOpen}
              onChange={(isOpen: boolean) => {
                updateCollapsible({ appearanceMonthsOpen: isOpen });
              }}
              title="Meses"
            >
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
                  return (
                    <div
                      className={clsx(["grid", "grid-cols-3", "gap-2"])}
                      key={month}
                    >
                      <div
                        className={clsx(["flex", "items-center", "text-sm"])}
                      >
                        {capitalize(`${moment(month, "MM").format("MMMM")}`)}
                      </div>
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {
                          setMonthColors({
                            ...monthColors,
                            [month]: {
                              ...monthColors[month],
                              bg: color.rgb,
                            },
                          });
                        }}
                        textColor={monthColors[month].color}
                        color={monthColors[month].bg}
                      />
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {
                          setMonthColors({
                            ...monthColors,
                            [month]: {
                              ...monthColors[month],
                              color: color.rgb,
                            },
                          });
                        }}
                        textColor={monthColors[month].bg}
                        color={monthColors[month].color}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock
              uppercase
              open={collapsiblePreferences.appearanceDaysOpen}
              onChange={(isOpen: boolean) => {
                updateCollapsible({ appearanceDaysOpen: isOpen });
              }}
              title="Días"
            >
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => {
                  return (
                    <div
                      className={clsx(["grid", "grid-cols-3", "gap-2"])}
                      key={day}
                    >
                      <div
                        className={clsx(["flex", "items-center", "text-sm"])}
                      >
                        {capitalize(`${moment(day, "DD").format("dddd")}`)}
                      </div>
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {
                          setDayColors({
                            ...dayColors,
                            [day]: {
                              ...dayColors[day],
                              bg: color.rgb,
                            },
                          });
                        }}
                        textColor={dayColors[day].color}
                        color={dayColors[day].bg}
                      />
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {
                          setDayColors({
                            ...dayColors,
                            [day]: {
                              ...dayColors[day],
                              color: color.rgb,
                            },
                          });
                        }}
                        textColor={dayColors[day].bg}
                        color={dayColors[day].color}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock title="Estado">
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                {transactionStatuses?.map?.((status: any) => {
                  return (
                    <div
                      className={clsx([
                        "grid",
                        "grid-cols-3",
                        "gap-2",
                        "flex",
                        "items-center",
                        "text-sm",
                      ])}
                      key={status?.id}
                    >
                      <div
                        className={clsx(["flex", "items-center", "text-sm"])}
                      >
                        {status?.name}
                      </div>
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {}}
                        textColor={SOURCES_DEFAULT_BG_COLOR}
                        color={SOURCES_DEFAULT_BG_COLOR}
                      />
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {}}
                        textColor={SOURCES_DEFAULT_BG_COLOR}
                        color={SOURCES_DEFAULT_BG_COLOR}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock title="Archivos">
              <div className={clsx(["flex", "flex-col", "gap-2"])}>
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-3",
                    "gap-2",
                    "flex",
                    "items-center",
                    "text-sm",
                  ])}
                >
                  <div></div>
                  <div>Fondo</div>
                  <div>Texto</div>
                </div>
                {fileTypeValues?.map?.((fileType: string) => {
                  return (
                    <div
                      className={clsx([
                        "grid",
                        "grid-cols-3",
                        "gap-2",
                        "flex",
                        "items-center",
                        "text-sm",
                      ])}
                      key={fileType}
                    >
                      <div
                        className={clsx(["flex", "items-center", "text-sm"])}
                      >
                        {fileType}
                      </div>
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {}}
                        textColor={SOURCES_DEFAULT_BG_COLOR}
                        color={SOURCES_DEFAULT_BG_COLOR}
                      />
                      <ColorPicker
                        showText={false}
                        onChange={(color: any) => {}}
                        textColor={SOURCES_DEFAULT_BG_COLOR}
                        color={SOURCES_DEFAULT_BG_COLOR}
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock title="Otros elementos">
              <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "md:text-base",
                    "text-sm",
                  ])}
                >
                  Otros elementos personalizables en la aplicación tienen sus
                  propias páginas de configuración. Puedes cambiar los colores
                  de las categorías, orígenes y destinos, métodos de pago y
                  cuentas utilizando los enlaces de la lista.
                </div>
                <TextList
                  items={[
                    {
                      text: "Categorías",
                      link: {
                        to: "/settings/categories",
                        type: ELinkType.INTERNAL,
                      },
                    },
                    {
                      text: "Orígenes y Destinos",
                      link: {
                        to: "/settings/sources",
                        type: ELinkType.INTERNAL,
                      },
                    },
                    {
                      text: "Métodos de pago",
                      link: {
                        to: "/settings/payment-methods",
                        type: ELinkType.INTERNAL,
                      },
                    },
                    {
                      text: "Cuentas",
                      link: {
                        to: "/settings/accounts",
                        type: ELinkType.INTERNAL,
                      },
                    },
                  ]}
                />
              </div>
            </CollapsibleBlock>

            <div
              className={clsx([
                "w-full",
                "flex",
                "justify-end",
                "bg-inherit",
                "md:py-3",
              ])}
            >
              <ButtonGroup
                buttons={[
                  {
                    text: t("common_buttons.update"),
                    onClick: () => {
                      setShowConfirmDialog(true);
                    },
                    icon: <MdCheck size={32} />,
                    kind: EButtonKind.SAVE,
                    isLoading: isUpdating,
                    disabled: isUpdating,
                    type: "button",
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </TwoSidebars>
      {showConfirmDialog && (
        <ConfirmDialog
          title={t("common_dialog_titles.update")}
          description={t("common_dialogs.save_changes")}
          onClose={() => {
            setShowConfirmDialog(false);
          }}
          confirmKind={EButtonKind.SAVE}
          onConfirm={() => {
            updateColors();
          }}
          isLoading={isUpdating}
        />
      )}
    </>
  );
};

export default TransactionsColorsPage;
